import { useToast } from '@ur-solutions/react-components'
import { useTranslate } from '@ur-solutions/react-hooks'
import React, { useEffect, useMemo, useRef } from 'react'
import { RegularModal } from '@/components_deprecated'
import { useDropzone } from '@/components/ui/dropzone'
import { UploadDropzone } from '@/components/upload-dropzone'
import { isDddFile } from '../../consts'
import { FileResultRow } from './FileResultRow'

const ERR_NOT_DDD = 'NOT_DDD'

const STATUS_ORDER = { pending: 0, error: 1, success: 2 } as const

export type TachoUploadOutcome =
  | { ok: true; id?: string }
  | { ok: false; error: string }

export type TachoFileUploadModalProps = {
  /** Modal title (variant-specific, e.g. "Upload vehicle files"). */
  title: string

  /** Performs the upload mutation. */
  upload: (file: File) => Promise<TachoUploadOutcome>

  /** Variant-specific pre-upload check beyond the shared .ddd check.
   * Return an error code (mapped by mapError) or null when valid. */
  validateFile?: (file: File) => string | null

  /** Maps a variant-specific error code to a user-facing message. Return
   * undefined to fall through to the shared mappings. */
  mapError?: (code: string) => string | undefined

  onClose: () => void
  onUploadSuccess?: () => void
}

export const TachoFileUploadModal: React.FC<TachoFileUploadModalProps> = ({
  title,
  upload,
  validateFile,
  mapError,
  onClose,
  onUploadSuccess,
}) => {
  const translations = useTranslate({
    alreadyUploaded: 'activities.already-uploaded',
    uploadFailedGeneric: 'activities.upload-failed-generic',
    uploadingStatus: 'activities.uploading-status',
    close: 'common.close',
    chooseFile: 'common.choose-file',
    onlyDddFilesAllowed: 'activities.only-ddd-files-allowed',
    filesUploadSuccessCount: [
      'activities.files-upload-success-count',
      { n: 0 },
    ],
    filesSummary: [
      'activities.files-summary',
      { total: 0, success: 0, failed: 0 },
    ],
  })

  const addToast = useToast()

  // Refs avoid stale-closure reads inside dropzone callbacks, which capture
  // values from the render where useDropzone was first called.
  const translationsRef = useRef(translations)
  const onCloseRef = useRef(onClose)
  const onUploadSuccessRef = useRef(onUploadSuccess)
  const uploadRef = useRef(upload)
  const validateFileRef = useRef(validateFile)
  const mapErrorRef = useRef(mapError)
  // Batch-scoped counters so onAllUploaded does not depend on React state
  // having committed by the time it fires, and so prior failed batches do
  // not block a later clean retry from auto-closing.
  const batchCountsRef = useRef({ success: 0, error: 0 })

  useEffect(() => {
    translationsRef.current = translations
    onCloseRef.current = onClose
    onUploadSuccessRef.current = onUploadSuccess
    uploadRef.current = upload
    validateFileRef.current = validateFile
    mapErrorRef.current = mapError
  })

  const dropzone = useDropzone<string | undefined, string>({
    onDropFile: async (file: File) => {
      try {
        if (!isDddFile(file.name)) {
          batchCountsRef.current.error += 1
          return { status: 'error', error: ERR_NOT_DDD }
        }

        const validationError = validateFileRef.current?.(file) ?? null
        if (validationError) {
          batchCountsRef.current.error += 1
          return { status: 'error', error: validationError }
        }

        const outcome = await uploadRef.current(file)
        if (!outcome.ok) {
          batchCountsRef.current.error += 1
          return { status: 'error', error: outcome.error }
        }

        batchCountsRef.current.success += 1
        return { status: 'success', result: outcome.id }
      } catch (e) {
        // upload() can throw (e.g. network errors or errorPolicy='none'); the
        // dropzone catches it and marks the file as error in its own state,
        // but we still need to record it in our batch counter so that
        // onAllUploaded does not treat the batch as fully successful.
        batchCountsRef.current.error += 1
        throw e
      }
    },
    shapeUploadError: (error: string) => {
      const t = translationsRef.current
      if (error === ERR_NOT_DDD) return t.onlyDddFilesAllowed

      const mapped = mapErrorRef.current?.(error)
      if (mapped) return mapped
      if (/already exists/i.test(error)) return t.alreadyUploaded
      if (process.env.NODE_ENV !== 'production' && error)
        console.warn('[TachoFileUploadModal] unmapped error:', error)
      return t.uploadFailedGeneric
    },
    onAllUploaded: () => {
      const { success, error } = batchCountsRef.current
      batchCountsRef.current = { success: 0, error: 0 }

      if (error === 0 && success > 0) {
        addToast(
          'success',
          translationsRef.current.filesUploadSuccessCount({ n: success })
        )
        onUploadSuccessRef.current?.()
        onCloseRef.current()
      } else if (success > 0) {
        onUploadSuccessRef.current?.()
      }
    },
  })

  const fileStatuses = dropzone.fileStatuses

  const sortedStatuses = useMemo(
    () =>
      [...fileStatuses].sort(
        (a, b) => STATUS_ORDER[a.status] - STATUS_ORDER[b.status]
      ),
    [fileStatuses]
  )

  const { successCount, failedCount } = useMemo(() => {
    let success = 0
    let failed = 0
    for (const s of fileStatuses) {
      if (s.status === 'success') success += 1
      else if (s.status === 'error') failed += 1
    }
    return { successCount: success, failedCount: failed }
  }, [fileStatuses])

  return (
    <RegularModal
      width="600px"
      title={title}
      cancelText={translations.close}
      onCancel={onClose}
      submitText={translations.chooseFile}
      submitIcon={{ icon: 'paperclip' }}
      onSubmit={() => dropzone.open()}
    >
      <div className="flex flex-col gap-6">
        <UploadDropzone dropzone={dropzone} accept=".ddd" />

        {fileStatuses.length > 0 && (
          <div className="flex flex-col gap-2">
            <p
              aria-live="polite"
              className="text-sm font-semibold text-foreground"
            >
              {translations.filesSummary({
                total: fileStatuses.length,
                success: successCount,
                failed: failedCount,
              })}
            </p>
            {sortedStatuses.map(status => (
              <FileResultRow
                key={status.id}
                status={status}
                uploadingLabel={translations.uploadingStatus}
              />
            ))}
          </div>
        )}
      </div>
    </RegularModal>
  )
}
